import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import styles from './style.module.scss';
import classNames from 'classnames';
import Heading from 'shared/components/common/heading/Heading';
import CustomInput from 'shared/components/common/custom_input/CustomInput';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import CustomSelect from 'shared/components/common/custom_selection/CustomSelection';
import { useSelector } from 'react-redux';
import ModalImage from 'shared/components/common/custom_image_lightbox/ModalImage';
import CustomTextarea from 'shared/components/common/custom_text_area/CustomTextArea';
import { UpdateTaskMutation } from 'shared/services/task_service/TaskService';
import { uploadFileMedia } from 'shared/services/media_service/Media_Service';
import UploadAvatarArray from 'shared/components/common/upload_avatar/UploadAvatarArray';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';

export default function TaskView({ show, tasks, handleClose = false, isAdmin = false }) {
    const {
        theme: { theme }
    } = useSelector((state) => state.root);
    const { mutate: updateTaskMutate, isLoading: UpdateTaskIsLoading, isSuccess: UpdateTaskIsSuccess } = UpdateTaskMutation();

    const [commentsChange, setCommentsChange] = useState('');
    const [avatarFileArray, setAvatarFileArray] = useState([]);
    const [diAvatarFileArray, setDiAvatarFileArray] = useState([]);
    const [selectedImg, setSelectedImg] = useState('');
    const [avatarFile, setAvatarFile] = useState(null);

    const [riUploadQueue, setRiUploadQueue] = useState([]);
    const [diUploadQueue, setDiUploadQueue] = useState([]);
    const riUploadQueueRef = useRef([]);
    const diUploadQueueRef = useRef([]);

    const clearUploadQueue = (items = []) => {
        items.forEach((queueItem) => {
            if (queueItem?.preview?.startsWith('blob:')) {
                URL.revokeObjectURL(queueItem.preview);
            }
        });
    };

    const resetUploadQueue = (setter, queueRef) => {
        clearUploadQueue(queueRef.current);
        setter([]);
    };

    useEffect(() => {
        riUploadQueueRef.current = riUploadQueue;
    }, [riUploadQueue]);

    useEffect(() => {
        diUploadQueueRef.current = diUploadQueue;
    }, [diUploadQueue]);

    useEffect(() => {
        if (!show) {
            resetUploadQueue(setRiUploadQueue, riUploadQueueRef);
            resetUploadQueue(setDiUploadQueue, diUploadQueueRef);
        }
    }, [show]);

    useEffect(() => {
        return () => {
            clearUploadQueue(riUploadQueueRef.current);
            clearUploadQueue(diUploadQueueRef.current);
        };
    }, []);

    useEffect(() => {
        if (tasks) {
            resetUploadQueue(setRiUploadQueue, riUploadQueueRef);
            resetUploadQueue(setDiUploadQueue, diUploadQueueRef);
            setCommentsChange(tasks.comments || '');
            setAvatarFileArray(tasks.rectifired_images || []);
            setDiAvatarFileArray(tasks.designed_images || []);
        }
    }, [tasks]);

    const uploadMediaArray = async (files, type) => {
        const setter = type === 'RI' ? setAvatarFileArray : setDiAvatarFileArray;
        const queueSetter = type === 'RI' ? setRiUploadQueue : setDiUploadQueue;
        const queueItems = files.map((file, index) => ({
            id: `${type}-${Date.now()}-${index}`,
            file,
            preview: URL.createObjectURL(file),
            status: 'waiting'
        }));

        queueSetter((prev) => [...prev, ...queueItems]);

        try {
            for (let [index, file] of files.entries()) {
                const currentQueueId = queueItems[index]?.id;

                queueSetter((prev) =>
                    prev.map((item) => (item.id === currentQueueId ? { ...item, status: 'uploading' } : item))
                );

                let fileBody = new FormData();
                fileBody.append("image", file);
                const imgfileArray = await uploadFileMedia(fileBody);
                const newurl = imgfileArray.data.url;

                queueSetter((prev) =>
                    prev.map((item) => (item.id === currentQueueId ? { ...item, status: 'uploaded', uploadedUrl: newurl } : item))
                );

                setter((prev) => [...prev, newurl]);

                setTimeout(() => {
                    queueSetter((prev) => {
                        const targetItem = prev.find((item) => item.id === currentQueueId);
                        if (targetItem?.preview?.startsWith('blob:')) {
                            URL.revokeObjectURL(targetItem.preview);
                        }
                        return prev.filter((item) => item.id !== currentQueueId);
                    });
                }, 900);
            }
        } catch (error) {
            console.error("Upload error:", error);
            queueSetter((prev) =>
                prev.map((item) =>
                    queueItems.some((queueItem) => queueItem.id === item.id && item.status !== 'uploaded')
                        ? { ...item, status: 'error' }
                        : item
                )
            );
        }
    };

    const handleSave = () => {
        let id = tasks?._id;
        let obj = {
            task_inventory: tasks?.task_inventory?._id,
            task_product: tasks.task_product?._id,
            liveStatus: tasks.liveStatus || 'Not Yet',
            priority: tasks.priority || 'Low',
            comments: commentsChange || '',
            rectifired_images: avatarFileArray,
            designed_images: diAvatarFileArray,
        };

        const payload = {
            id: id,
            body: obj
        };
        updateTaskMutate(payload);
    };

    useEffect(() => {
        if (UpdateTaskIsSuccess) {
            handleClose();
        }
    }, [UpdateTaskIsSuccess]);

    const renderAssigned = () => {
        if (!tasks?.task_assigned?.name) return 'Unassigned';
        return <>{tasks.task_assigned.name} <sup style={{fontWeight: 'bold'}}>{tasks.task_point}</sup></>;
    };

    const renderEarnedPoints = () => {
        if (tasks?.status !== 'COMPLETED') return '-';
        if (!tasks?.task_assigned_date) return tasks?.task_point;
        
        let expectedHours = 0, expectedMinutes = 0;
        const task_point = String(tasks?.task_point || "0");
        task_point.split('.').forEach((point, inx) => {
            if (Number(point) && inx === 0) expectedHours = Number(point);
            if (Number(point) && inx) expectedMinutes = Number(point);
        });

        const expectedSecondsTotal = expectedHours * 3600 + expectedMinutes * 60;
        if (expectedSecondsTotal === 0) return task_point;

        const assignedDateMs = new Date(tasks.task_assigned_date).getTime();
        const completedDateMs = new Date(tasks.task_completed_date || new Date()).getTime();
        
        let elapsedSeconds = (completedDateMs - assignedDateMs) / 1000;
        if (elapsedSeconds < 0) elapsedSeconds = 0;

        if (elapsedSeconds <= expectedSecondsTotal) {
            return <span style={{ color: '#5cb85c', fontWeight: 'bold' }}>{task_point}</span>;
        } else {
            const extraTime = elapsedSeconds - expectedSecondsTotal;
            const penaltyPercent = extraTime / expectedSecondsTotal;
            const maxPoints = Number(task_point) || 0;
            let earned = maxPoints - (maxPoints * penaltyPercent);
            if (earned < 0) earned = 0;
            
            return (
                <span style={{ color: '#d9534f', fontWeight: 'bold' }} title={`Delayed Penalty: -${(penaltyPercent * 100).toFixed(0)}%`}>
                    {earned.toFixed(2).replace(/\.00$/, '')}
                </span>
            );
        }
    };

    const renderTimeDetails = () => {
        if (tasks?.status === 'COMPLETED' && tasks?.task_assigned_date) {
            let expectedHours = 0, expectedMinutes = 0;
            const task_point = String(tasks?.task_point || "0");
            task_point.split('.').forEach((point, inx) => {
                if (Number(point) && inx === 0) expectedHours = Number(point);
                if (Number(point) && inx) expectedMinutes = Number(point);
            });

            const assignedDateMs = new Date(tasks.task_assigned_date).getTime();
            const completedDateMs = new Date(tasks.task_completed_date || new Date()).getTime();
            
            let elapsedSeconds = (completedDateMs - assignedDateMs) / 1000;
            if (elapsedSeconds < 0) elapsedSeconds = 0;
            
            const elHours = Math.floor(elapsedSeconds / 3600);
            const elMinutes = Math.floor((elapsedSeconds % 3600) / 60);
            const elSeconds = Math.floor(elapsedSeconds % 60);

            return (
                <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                    <div style={{ color: '#555' }}><b>Total:</b> {expectedHours}h {expectedMinutes}m</div>
                    <div style={{ color: '#d9534f' }}><b>Taken:</b> {elHours}h {elMinutes}m {elSeconds}s</div>
                </div>
            );
        }
        return '-';
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Header className={styles.Header}>
                <Heading heading={'Task Detail'} headingStyle={styles.AddLinkHeading} />
            </Modal.Header>
            <Modal.Body className={classNames(`${theme}ModalBackground`, styles.Body)}>
                <div className={classNames(styles.AddLinkContainer)}>
                    <div className={styles.DetailsGrid}>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>SKU</span>
                            <span className={styles.DetailValue}>{tasks?.task_inventory?.sku || tasks?.task_sku || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Brand</span>
                            <span className={styles.DetailValue}>{tasks?.task_brand?.name || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Compatible Brand</span>
                            <span className={styles.DetailValue}>{tasks?.task_compatiableBrand?.name || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Model</span>
                            <span className={styles.DetailValue}>{tasks?.task_product?.name || tasks?.task_product?.model || (tasks?.name && tasks?.name !== 'Rough Listing Item' ? tasks?.name : '') || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Category</span>
                            <span className={styles.DetailValue}>{tasks?.task_category?.name || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Sub Category</span>
                            <span className={styles.DetailValue}>{tasks?.task_subcategory?.name || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Color</span>
                            <span className={styles.DetailValue}>{tasks?.task_inventory?.color || tasks?.task_color || tasks?.color || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Task Priority</span>
                            <span className={styles.DetailValue}>{tasks?.priority || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Task Status</span>
                            <span className={styles.DetailValue}>{tasks?.status || 'N/A'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Task Points</span>
                            <span className={styles.DetailValue}>{tasks?.task_point || '0'}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Assigned To</span>
                            <span className={styles.DetailValue}>{renderAssigned()}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Earned Points</span>
                            <span className={styles.DetailValue}>{renderEarnedPoints()}</span>
                        </div>
                        <div className={styles.DetailItem}>
                            <span className={styles.DetailLabel}>Time Details</span>
                            <span className={styles.DetailValue}>{renderTimeDetails()}</span>
                        </div>
                    </div>
                    {isAdmin ? (
                        <CustomTextarea
                            Label={'Comments'}
                            type={'text'}
                            Placeholder={'Comments'}
                            Value={commentsChange}
                            OnChange={(e) => {
                                setCommentsChange(e.target.value)
                            }}
                            CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                            CustomLabelStyle={styles.CustomLabelStyle}
                            InputContainerStyle={styles.InputContainerStyle}
                        />
                    ) : (
                        <div className={styles.DetailItemFull} style={{ marginTop: '15px' }}>
                            <span className={styles.DetailLabel} style={{ fontWeight: 'bold' }}>Comments</span>
                            <span className={styles.DetailValue}>
                                {commentsChange || 'No Comments'}
                            </span>
                        </div>
                    )}
                    
                    {/* --- Raw Images (from Rough Listing) --- */}
                    {tasks?.raw_images && tasks.raw_images.length > 0 && (
                        <div className="mt-3">
                            <label className={styles.CustomLabelStyle} style={{ fontWeight: 'bold' }}>
                                Raw Images <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'normal' }}>(from Rough Listing)</span>
                            </label>
                            <div className={classNames(styles.TableFlex, 'mt-2')}>
                                {tasks.raw_images.map((item, idx) => (
                                    <div key={idx} className={`p-0 ${styles.Avatar}`} role={'button'}>
                                        <label className={classNames('position-relative', styles.UploadAvatarContainer)} role={'button'}>
                                            <ModalImage
                                                small={item}
                                                large={item}
                                                alt="Raw Image"
                                                className={classNames(styles.profileImages)}
                                                imageArray={tasks.raw_images}
                                                currentIndex={idx}
                                            />
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-3">
                        <label className={styles.CustomLabelStyle} style={{ fontWeight: 'bold' }}>Rectifired Images</label>
                        {isAdmin ? (
                            <UploadAvatarArray
                                id="ri_images"
                                setAvatarFileArray={setAvatarFileArray}
                                edittask={true}
                                UploadedFile={avatarFile}
                                avatarFileArray={avatarFileArray}
                                setArray={(files) => uploadMediaArray(files, 'RI')}
                                setFile={setAvatarFile}
                                selectedImg={selectedImg}
                                setSelectedImg={setSelectedImg}
                                uploadQueue={riUploadQueue}
                            />
                        ) : (
                            <div className={classNames(styles.TableFlex, 'mt-2')}>
                                {avatarFileArray.length > 0 ? (
                                    avatarFileArray.map((item, idx) => (
                                        <div key={idx} className={`p-0 ${styles.Avatar}`} role={'button'}>
                                            <label className={classNames('position-relative', styles.UploadAvatarContainer)} role={'button'}>
                                                <ModalImage 
                                                    small={item} 
                                                    large={item} 
                                                    alt="Rectified Image" 
                                                    className={classNames(styles.profileImages)} 
                                                    imageArray={avatarFileArray}
                                                    currentIndex={idx}
                                                />
                                            </label>
                                        </div>
                                    ))
                                ) : (
                                    <span style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic' }}>No Rectified Images</span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="mt-3">
                        <label className={styles.CustomLabelStyle} style={{ fontWeight: 'bold' }}>Designed Images</label>
                        {isAdmin ? (
                            <UploadAvatarArray
                                id="di_images"
                                setAvatarFileArray={setDiAvatarFileArray}
                                edittask={true}
                                UploadedFile={avatarFile}
                                avatarFileArray={diAvatarFileArray}
                                setArray={(files) => uploadMediaArray(files, 'DI')}
                                setFile={setAvatarFile}
                                selectedImg={selectedImg}
                                setSelectedImg={setSelectedImg}
                                uploadQueue={diUploadQueue}
                            />
                        ) : (
                            <div className={classNames(styles.TableFlex, 'mt-2')}>
                                {diAvatarFileArray.length > 0 ? (
                                    diAvatarFileArray.map((item, idx) => (
                                        <div key={idx} className={`p-0 ${styles.Avatar}`} role={'button'}>
                                            <label className={classNames('position-relative', styles.UploadAvatarContainer)} role={'button'}>
                                                <ModalImage 
                                                    small={item} 
                                                    large={item} 
                                                    alt="Designed Image" 
                                                    className={classNames(styles.profileImages)} 
                                                    imageArray={diAvatarFileArray}
                                                    currentIndex={idx}
                                                />
                                            </label>
                                        </div>
                                    ))
                                ) : (
                                    <span style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic' }}>No Designed Images</span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        {isAdmin ? (
                            <>
                                <CustomButton text={'Cancel'} containerStyle={styles.CancelBtn} submitHandle={handleClose} />
                                <CustomButton
                                    text={'Save Changes'}
                                    containerStyle={styles.SubmitBtn}
                                    submitHandle={() => {
                                        handleSave();
                                    }}
                                />
                            </>
                        ) : (
                            <CustomButton text={'Close'} containerStyle={styles.CancelBtn} submitHandle={handleClose} />
                        )}
                    </div>
                </div>
                {UpdateTaskIsLoading && <OverlayLoading />}
            </Modal.Body>
        </Modal>
    );
}
